import type { AppMode } from '@bmkg/core';

// __APP_MODE__ disuntik via `define` di vite.config.ts / vitest.config.ts.
declare const __APP_MODE__: AppMode;

export const APP_MODE: AppMode = __APP_MODE__;

/**
 * Konfigurasi runtime untuk integrasi opsional. Vite membakar `VITE_*` saat
 * build, tetapi hosting statis sering perlu mengaktifkan/mematikan integrasi
 * tanpa build ulang (feature flag PRD Bagian 11). Bila `window.__SMH_ENV__`
 * diset sebelum bundle dimuat, nilainya menang; kalau tidak, nilai build dipakai.
 * E2E memakai ini untuk mengaktifkan Turnstile hanya pada tes yang mengujinya,
 * supaya halaman lain tetap tanpa permintaan ke domain pihak ketiga.
 */
interface RuntimeEnv {
  readonly [name: string]: string | undefined;
}

function runtimeValue(name: string): string | undefined {
  const bag = (globalThis as { __SMH_ENV__?: RuntimeEnv }).__SMH_ENV__;
  const value = bag?.[name];
  return typeof value === 'string' ? value : undefined;
}

/** Baca env klien: runtime override menang atas env build. */
export function clientEnv(
  name:
    | 'VITE_SUPABASE_URL'
    | 'VITE_SUPABASE_ANON_KEY'
    | 'VITE_EDGE_FUNCTION_URL'
    | 'VITE_TURNSTILE_SITE_KEY'
    | 'VITE_MODERATION_MODE'
    | 'VITE_ENABLE_REALTIME'
    | 'VITE_DASHBOARD_POLL_MS'
    | 'VITE_BMKG_PROXY_BASE'
    | 'VITE_SENTRY_DSN',
): string {
  return runtimeValue(name) ?? import.meta.env[name] ?? '';
}

/** Mode A: endpoint Supabase (publik). Nama akhir difinalkan di CP06. */
export const SUPABASE_URL: string = clientEnv('VITE_SUPABASE_URL');
/** Mode A: kunci publik Supabase (aman di bundle; RLS yang menjaga data). */
export const SUPABASE_ANON_KEY: string = clientEnv('VITE_SUPABASE_ANON_KEY');
/** Mode A: URL Edge Function submit. Nama akhir difinalkan di CP06. */
export const EDGE_FUNCTION_URL: string = clientEnv('VITE_EDGE_FUNCTION_URL');
/** Cloudflare Turnstile site key (publik). Kosong → widget dinonaktifkan. */
export const TURNSTILE_SITE_KEY: string = clientEnv('VITE_TURNSTILE_SITE_KEY');

export const MODERATION_MODE: boolean = clientEnv('VITE_MODERATION_MODE') === 'on';
export const ENABLE_REALTIME: boolean = clientEnv('VITE_ENABLE_REALTIME') !== 'off';

/** Jeda fallback polling (ms). E2E memperpendek lewat konfigurasi runtime. */
export const DASHBOARD_POLL_MS: number = Math.max(
  1_000,
  Number(clientEnv('VITE_DASHBOARD_POLL_MS')) || 20_000,
);

/**
 * Mode B: dasar URL proxy BMKG (CP09). Kosong → dashboard BMKG tampil dengan
 * pesan jujur "proxy belum dikonfigurasi", bukan data palsu (pola Mode A).
 * E2E mengganti lewat `window.__SMH_ENV__` agar siap untuk semua panel.
 */
export const BMKG_PROXY_BASE: string = clientEnv('VITE_BMKG_PROXY_BASE').replace(/\/+$/, '');

/** Mode B: proxy BMKG siap dipakai? */
export function isBmkgProxyConfigured(): boolean {
  return BMKG_PROXY_BASE.length > 0;
}

/** Backend laporan siap dipakai? Dipakai untuk memutuskan jalur submit. */
export function isReportBackendConfigured(): boolean {
  return EDGE_FUNCTION_URL.trim().length > 0 || SUPABASE_URL.trim().length > 0;
}

/**
 * Mode A: dashboard punya backend baca (Supabase/PostgREST)? Bila tidak,
 * `CommunitySource` menyajikan keadaan jujur "backend belum dikonfigurasi"
 * dengan data demo yang ditandai jelas (spec CP08).
 */
export function isCommunitySourceConfigured(): boolean {
  return SUPABASE_URL.trim().length > 0;
}
